import { QP_CALENDAR_VIEW_MODE, TTF } from '@/constants/queryParams';
import { ROUTE_CALENDAR_LIST } from '@/constants/routes';
import useAuth from '@/hooks/useAuth';
import { useSearchParams } from '@/hooks/useSearchParams';
import { useCreateCalendarEventMutation, useRemoveCalendarEventMutation } from '@/queries/calendar-event.queries';
import { useRemoveCalendarMutation } from '@/queries/calendar.queries';
import calendarEventsService from '@/services/calendar-events/calendar-events.service';
import { createInstant } from '@/timetime/timetime';
import { ICalendar } from '@/types/ICalendar';
import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import { ArrayWithAtLeastOneItem } from '@/types/utils/utility-types';
import { useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Temporal } from 'temporal-polyfill';

export const VISIBLE_DAYS = 5;

const QP_EDIT_CALENDAR = 'editCalendar';

interface IArgs {
	calendars: ArrayWithAtLeastOneItem<ICalendar>;
	events: Array<ICalendarEvent>;
}

interface IState {
	events: Array<ICalendarEvent>;
	selectedEvent: ICalendarEvent | null;
}

export function useBookingPlannerController(args: IArgs) {
	const toast = useToast();
	const history = useHistory();
	const auth = useAuth();
	const params = useSearchParams();
	const from = params.get(TTF) || Temporal.Now.plainDateISO().toString();
	const fromInstant = createInstant({ timestamp: from, timezone: auth.getCurrentUser().timeZone });
	const hidePending = params.get('hidePending');
	const removeCalendarEventMutation = useRemoveCalendarEventMutation();
	const removeCalendarMutation = useRemoveCalendarMutation();
	const editCalendarEventMutation = useCreateCalendarEventMutation();
	// The URL contains an array of visible calendars
	const calendarIds = params.get('ids');
	// At the moment we only support one single calendar
	const calendarId = calendarIds?.split(',')[0] as string;
	// We expect the calendar to be in the list but a wrong URL can be formed
	const calendar = args.calendars.find((c) => c.id === calendarId) as ICalendar;

	const form = useForm<IState>({
		defaultValues: {
			selectedEvent: undefined,
		},
	});

	function handleEventClick(calendarEvent: ICalendarEvent) {
		form.setValue('selectedEvent', calendarEvent);
		params.set('event-id', calendarEvent.id);
		params.set('calendar-id', calendarEvent.calendarId);
		history.replace({ search: params.toString() });
	}

	function handleCloseClick() {
		_closeEvent();
	}

	function _closeEvent() {
		form.setValue('selectedEvent', null);
		params.delete('event-id');
		history.push({ search: params.toString() });
	}

	function _openEvent(event: ICalendarEvent) {
		form.setValue('selectedEvent', event);
		params.set('event-id', event.id);
		history.replace({ search: params.toString() });
	}

	function handleEditCalendarCloseClick() {
		params.delete(QP_EDIT_CALENDAR);
		history.replace({ search: params.toString() });
	}

	function handleNextClick() {
		const tomorrow = Temporal.PlainDate.from(from).add({ days: 1 });
		params.set(TTF, tomorrow.toString());
		history.push({ search: params.toString() });
	}

	function handlePrevClick() {
		const yesterday = Temporal.PlainDate.from(from).subtract({ days: 1 });
		params.set(TTF, yesterday.toString());
		history.push({ search: params.toString() });
	}

	function handleCreateEventClick() {
		if (!calendar) {
			throw Error('No calendar found. Ensure the URL contains a calendar ID');
		}

		const calendarEvent = calendarEventsService.createLocalCalendarEvent({ calendarId: calendar.id }) as ICalendarEvent;

		if (calendar?.tags.TT_PLANNER_MODE === 'DRAFT') {
			calendarEvent.status = 'TENTATIVE';
		}

		return _saveEvent(calendarEvent).then(() => _openEvent(calendarEvent));
	}

	function handleMonthClick() {
		params.set(QP_CALENDAR_VIEW_MODE, 'month');
		history.push({ search: params.toString() });
	}

	function handleShowPendingClick() {
		hidePending ? params.delete('hidePending') : params.set('hidePending', 'true');
		history.replace({ search: params.toString() });
	}

	function handleEditCalendarClick() {
		params.set(QP_EDIT_CALENDAR, calendarId);
		history.replace({ search: params.toString() });
	}

	function handleRemoveCalendarClick() {
		if (!window.confirm(t('Are you sure? This will DELETE the calendar an all the events.'))) {
			return;
		}
		return removeCalendarMutation
			.mutateAsync({
				id: calendarId,
			})
			.then(() => {
				toast({
					status: 'success',
					title: t('Calendar deleted'),
				});
				history.push(ROUTE_CALENDAR_LIST);
			});
	}

	function _saveEvent(event: ICalendarEvent) {
		const mutationPromise = editCalendarEventMutation.mutateAsync(event).then(() => {
			_closeEvent();
		});

		toast.promise(mutationPromise, {
			success: { title: t('Saved changes') },
			error: { title: t('Error') },
			loading: { title: t('...') },
		});

		return mutationPromise;
	}

	function handleEventSave(event: ICalendarEvent) {
		return _saveEvent(event);
	}

	function handleEventDelete(event: ICalendarEvent) {
		return removeCalendarEventMutation.mutateAsync({ id: event.id }).then(_closeEvent);
	}

	function handleSaveAllClick() {
		const promises = args.events
			.filter((e) => e.status === 'TENTATIVE')
			.map((event) => editCalendarEventMutation.mutateAsync({ ...event, status: 'CONFIRMED' }));

		toast.promise(Promise.all(promises), {
			success: { title: t('Saved changes') },
			error: { title: t('Error') },
			loading: { title: t('...') },
		});
	}

	function handleEventDrop(event: ICalendarEvent) {
		/**
		 * IF the event is dropped is because the date was changed.
		 */
		if (calendar.tags.TT_STATUS_WHEN_DATES_CHANGED === 'SET_TENTATIVE') {
			event.status = 'TENTATIVE';
		}
		return _saveEvent(event);
	}

	return {
		handleEventDrop,
		handleCloseClick,
		handleCreateEventClick,
		handleEditCalendarClick,
		handleEditCalendarCloseClick,
		handleEventClick,
		handleEventDelete,
		handleMonthClick,
		handleNextClick,
		handlePrevClick,
		handleRemoveCalendarClick,
		handleEventSave,
		handleShowPendingClick,
		handleSaveAllClick,
		selectedEvent: form.watch('selectedEvent'),
		calendar,
		calendars: args.calendars,
		events: args.events.filter((e) => !!e.interval),
		pendingEvents: args.events.filter((e) => !e.interval),
		fromInstant,
		hidePending: !!hidePending,
		// The modal is open when the url contains the ID of an event
		isModalOpen: !!params.get('event-id'),
		isEditCalendarModalOpen: !!params.get(QP_EDIT_CALENDAR),
	};
}
